import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect } from 'react';
import { SimpleGrid, Flex, Field } from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import { Button as ButtonComponent, SectionCard, InputField } from '@themes/aegix';
import { useUpdateRoom } from '../../../hooks/queries/useRooms';
import type { Room } from '../../../services';
import { toaster } from '../../ui/toaster';

interface RoomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  room: Room | null;
}

export function RoomDrawer({ isOpen, onClose, room }: RoomDrawerProps) {
  const { t } = useTranslation();
  const [roomName, setRoomName] = useState('');
  const [dirty, setDirty] = useState(false);

  const updateRoomMutation = useUpdateRoom();

  useEffect(() => {
    if (room) {
      setRoomName(room.name);
      setDirty(false);
    }
  }, [room]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveRoom = useCallback(() => {
    if (!room || !roomName.trim()) return;
    updateRoomMutation.mutate(
      { id: room.id, data: { name: roomName.trim() } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Room updated', type: 'success', duration: 3000 });
          setDirty(false);
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [room, roomName, updateRoomMutation]);

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={room ? `Room — ${room.name}` : 'Room'}
      size="md"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        <SimpleGrid columns={1} gap={4} width="100%">
          <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-room-drawer-button">
            {t('common.close', 'Close')}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <SectionCard title={t('sites.roomDetails', 'Room Details')}>
        <Field.Root>
          <Field.Label htmlFor="room-name">{t('common.name', 'Name')}</Field.Label>
          <InputField
            id="room-name"
            size="sm"
            value={roomName}
            onChange={(e) => { setRoomName(e.target.value); setDirty(true); }}
            data-e2e="room-name-input"
          />
        </Field.Root>
        {dirty && (
          <Flex gap={2} justifyContent="flex-end" mt={3}>
            <ButtonComponent
              visual="secondary"
              size="sm"
              onClick={() => {
                if (room) { setRoomName(room.name); setDirty(false); }
              }}
              data-e2e="cancel-room-details-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              size="sm"
              onClick={handleSaveRoom}
              disabled={updateRoomMutation.isPending}
              data-e2e="save-room-details-button"
            >
              {updateRoomMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </ButtonComponent>
          </Flex>
        )}
      </SectionCard>
    </DrawerComponent>
  );
}
